// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pieces of the task detail view: title, status pill, property rows,
// description, subtasks, time tracking, activity and the comment composer, plus
// the small editors behind the rows. The phone sheet and the desktop modal lay
// the same pieces out differently; editors open as bottom sheets on the phone
// and as popovers on the desktop.

import {
	openTaskAttachment,
	TaskDescriptionMarkdown,
} from '@app/features/konvo_tasks/components/task_sheet/TaskDescriptionMarkdown';
import {
	dueQuickPicks,
	dueTone,
	firstName,
	fmtBytes,
	fmtDay,
	fmtDue,
	fmtRelative,
	fmtShortDay,
	fmtStamp,
	fmtTime,
	fmtTimer,
	fromDateAndTime,
	initialsOf,
	priorityColor,
	subtaskProgress,
	TASK_PRIORITIES,
	toDateInputValue,
	toTimeInputValue,
} from '@app/features/konvo_tasks/components/task_sheet/TaskFormat';
import styles from '@app/features/konvo_tasks/components/task_sheet/TaskParts.module.css';
import {
	type TaskWorkspace,
	TIMER_LINK_REQUIRED_DESCRIPTOR,
} from '@app/features/konvo_tasks/components/task_sheet/useTaskWorkspace';
import {CommentsSkeleton, SectionEmpty} from '@app/features/konvo_tasks/components/workspace/shared/TaskNotice';
import {
	formatDuration,
	isTaskClosed,
	type KonvoTaskUser,
	type KonvoTimeEntry,
	statusLabel,
} from '@app/features/konvo_tasks/state/KonvoTasks';
import {fitComposerHeight} from '@app/features/platform/utils/ComposerHeight';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {
	ArrowUpIcon,
	AtIcon,
	CalendarBlankIcon,
	CaretDownIcon,
	CheckCircleIcon,
	CheckIcon,
	ClockIcon,
	FileIcon,
	FlagIcon,
	PaperclipIcon,
	PauseIcon,
	PencilSimpleIcon,
	PlayIcon,
	PlusIcon,
	SparkleIcon,
	StopIcon,
	TagIcon,
	TrashIcon,
	UsersIcon,
} from '@app/features/ui/icons/Icons';
import {ActionButton, IconButton} from '@app/features/ui/konvo_controls/KonvoControls';
import {Picture} from '@app/features/ui/picture/Picture';
import type {PopoutPosition} from '@app/features/ui/popover';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconClock, IconMessageCircle} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';

export const TASK_DESCRIPTORS = {
	status: msg({message: 'Status', comment: 'Task detail editor title.'}),
	priority: msg({message: 'Priority', comment: 'Task detail property row label.'}),
	due: msg({message: 'Due', comment: 'Task detail property row label for the due date.'}),
	assignees: msg({message: 'Assignees', comment: 'Task detail property row label.'}),
	time: msg({message: 'Time', comment: 'Task detail property row label for tracked time.'}),
	tags: msg({message: 'Tags', comment: 'Task detail property row label.'}),
	description: msg({message: 'Description', comment: 'Task card section title.'}),
	attachments: msg({message: 'Attachments', comment: 'Task card section title.'}),
	subtasks: msg({message: 'Subtasks', comment: 'Task card section title.'}),
	activity: msg({message: 'Activity', comment: 'Task card section title.'}),
	timeTracking: msg({message: 'Time tracking', comment: 'Task card section title.'}),
	logTime: msg({message: 'Log time', comment: 'Task card button that opens or saves a manual time entry.'}),
	editEntry: msg({message: 'Edit time entry', comment: 'Task detail editor title for an existing time entry.'}),
	more: msg({message: 'More', comment: 'Task detail overflow menu button label.'}),
	openInClickUp: msg({message: 'Open in ClickUp', comment: 'Task card button.'}),
	close: msg({message: 'Close', comment: 'Task detail close button label.'}),
	attach: msg({message: 'Attach file', comment: 'Task card button.'}),
	mention: msg({message: 'Mention someone', comment: 'Task detail comment composer button that inserts an @mention.'}),
};

export const NONE_DESCRIPTOR = msg({message: 'None', comment: 'Task card value when a field is empty.'});
const NO_DUE_DESCRIPTOR = msg({message: 'No date', comment: 'Task detail due row value when there is no due date.'});
const ADD_DESCRIPTOR = msg({message: 'Add', comment: 'Task detail assignees row value when nobody is assigned.'});
export const CREATED_BY_DESCRIPTOR = msg({
	message: 'created by {name}',
	comment: 'Task detail meta line after the task key. {name} is a person.',
});
const MARK_COMPLETE_DESCRIPTOR = msg({message: 'Mark complete', comment: 'Task card button.'});
export const COMPLETED_DESCRIPTOR = msg({
	message: 'Completed',
	comment: 'Task card state label when the task is closed.',
});
const LOGGED_DESCRIPTOR = msg({
	message: '{duration} logged',
	comment: 'Task detail time row value. {duration} is e.g. "1h 30m".',
});
const START_DESCRIPTOR = msg({message: 'Start', comment: 'Task detail button that starts the ClickUp timer.'});
const START_TIMER_DESCRIPTOR = msg({message: 'Start timer', comment: 'Task card time tracking button.'});
const STOP_DESCRIPTOR = msg({message: 'Stop', comment: 'Task card time tracking button that stops the timer.'});
const PAUSE_DESCRIPTOR = msg({message: 'Pause', comment: 'Task card time tracking button that pauses the timer.'});
const RUNNING_SINCE_DESCRIPTOR = msg({
	message: 'Running since {time}',
	comment: 'Task detail running timer caption. {time} is a clock time.',
});
const TIMER_ELSEWHERE_DESCRIPTOR = msg({
	message: 'Timer running on “{name}”',
	comment: 'Task card hint when the user already has a ClickUp timer running on another task. {name} is that task.',
});
const TOTAL_DESCRIPTOR = msg({
	message: '{duration} total',
	comment: 'Task detail time tracking section total. {duration} is e.g. "1h 30m".',
});
const ESTIMATE_DESCRIPTOR = msg({
	message: 'of {estimate}',
	comment: 'Task detail time total suffix when the task has an estimate. {estimate} is e.g. "4h".',
});
const NO_TIME_DESCRIPTOR = msg({message: 'No time logged yet.', comment: 'Task detail empty time tracking state.'});
const DESCRIPTION_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Add a description…',
	comment: 'Task card description placeholder.',
});
const DESCRIPTION_DROP_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Add a description… drop files here to attach them.',
	comment: 'Task card description placeholder on desktop.',
});
const SAVE_DESCRIPTOR = msg({message: 'Save', comment: 'Task card button.'});
const EDIT_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Edit description',
	comment: 'Task detail button that switches the formatted description to its editable text.',
});
const CANCEL_DESCRIPTOR = msg({message: 'Cancel', comment: 'Task card button.'});
export const PROGRESS_DESCRIPTOR = msg({
	message: '{done} of {total}',
	comment: 'Task detail subtask progress, e.g. "1 of 3".',
});
const ADD_SUBTASK_DESCRIPTOR = msg({message: 'Add subtask', comment: 'Task detail button and input label.'});
const SUBTASK_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Subtask name',
	comment: 'Placeholder of the inline new-subtask input.',
});
export const MARK_SUBTASK_DONE_DESCRIPTOR = msg({
	message: 'Mark {name} complete',
	comment: 'Accessible label of a subtask checkbox. {name} is the subtask.',
});
export const MARK_SUBTASK_OPEN_DESCRIPTOR = msg({
	message: 'Mark {name} not complete',
	comment: 'Accessible label of a ticked subtask checkbox. {name} is the subtask.',
});
const LOADING_COMMENTS_DESCRIPTOR = msg({
	message: 'Loading comments',
	comment: 'Task card: accessible name of the skeleton while the comments load.',
});
const NO_COMMENTS_DESCRIPTOR = msg({
	message: 'No comments yet. Write the first one below.',
	comment: 'Task card empty comments state.',
});
const COMMENT_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Comment or @mention',
	comment: 'Task detail comment composer placeholder.',
});
const SEND_COMMENT_DESCRIPTOR = msg({message: 'Send comment', comment: 'Task detail comment send button label.'});
const DURATION_LABEL_DESCRIPTOR = msg({
	message: 'Duration',
	comment: 'Field label above the manual time entry duration input.',
});
const DURATION_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'e.g. 1h 30m',
	comment: 'Placeholder for the manual time entry duration input.',
});
const WHEN_LABEL_DESCRIPTOR = msg({
	message: 'Started',
	comment: 'Field label above the manual time entry start input. Empty means now.',
});
const WHEN_HINT_DESCRIPTOR = msg({
	message: 'Leave empty to log it as just finished.',
	comment: 'Hint under the manual time entry start input.',
});
const NOTE_LABEL_DESCRIPTOR = msg({message: 'Note', comment: 'Field label above the manual time entry note input.'});
const NOTE_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'What did you work on?',
	comment: 'Placeholder for the manual time entry note input.',
});
const DELETE_ENTRY_DESCRIPTOR = msg({message: 'Delete entry', comment: 'Task card time entry delete button label.'});
const DATE_LABEL_DESCRIPTOR = msg({message: 'Date', comment: 'Task detail due editor date field label.'});
const TIME_LABEL_DESCRIPTOR = msg({
	message: 'Time (optional)',
	comment: 'Task detail due editor time field label.',
});
const CLEAR_DUE_DESCRIPTOR = msg({message: 'Remove due date', comment: 'Task detail due editor button.'});
const DUE_PICK_DESCRIPTORS = {
	today: msg({message: 'Today', comment: 'Task detail due editor quick pick.'}),
	tomorrow: msg({message: 'Tomorrow', comment: 'Task detail due editor quick pick.'}),
	monday: msg({message: 'Next Monday', comment: 'Task detail due editor quick pick.'}),
	week: msg({message: 'In a week', comment: 'Task detail due editor quick pick.'}),
};
export const PRIORITY_DESCRIPTORS: Record<number, ReturnType<typeof msg>> = {
	1: msg({message: 'Urgent', comment: 'ClickUp task priority.'}),
	2: msg({message: 'High', comment: 'ClickUp task priority.'}),
	3: msg({message: 'Normal', comment: 'ClickUp task priority.'}),
	4: msg({message: 'Low', comment: 'ClickUp task priority.'}),
};
const NO_MEMBERS_DESCRIPTOR = msg({
	message: 'No ClickUp members found.',
	comment: 'Task detail assignee editor empty state.',
});

// ── Small pieces ──────────────────────────────────────────────────────────

export const UserAvatar = ({user, size = 26}: {user: KonvoTaskUser | null; size?: number}) => {
	const initials = user?.initials ?? initialsOf(user?.username ?? user?.email);
	const style = {width: size, height: size, fontSize: Math.round(size * 0.38)} as React.CSSProperties;
	if (user?.profile_picture) {
		return <img src={user.profile_picture} alt="" className={styles.avatar} style={style} />;
	}
	return (
		<span
			className={styles.avatar}
			style={{...style, ...(user?.color ? ({'--av': user.color} as React.CSSProperties) : {})}}
			aria-hidden="true"
		>
			{initials}
		</span>
	);
};

function AvatarPile({users, max = 4}: {users: Array<KonvoTaskUser>; max?: number}) {
	return (
		<span className={styles.pile}>
			{users.slice(0, max).map((user) => (
				<UserAvatar key={user.id ?? user.username ?? ''} user={user} size={26} />
			))}
			{users.length > max && <span className={styles.pileMore}>+{users.length - max}</span>}
		</span>
	);
}

export function PriorityFlag({id, size = 15}: {id: number | null | undefined; size?: number}) {
	const color = priorityColor(id);
	return <FlagIcon size={size} weight={color ? 'fill' : 'regular'} color={color ?? undefined} aria-hidden="true" />;
}

/** Turns bare URLs in comment/description text into links. */
export function linkify(text: string): Array<React.ReactNode> {
	const parts = text.split(/(https?:\/\/[^\s<>"')\]]+)/g);
	return parts.map((part, index) =>
		/^https?:\/\//.test(part) ? (
			<a
				key={`${index}-${part}`}
				href={part}
				className={styles.textLink}
				onClick={(event) => {
					event.preventDefault();
					void openExternalUrl(part);
				}}
			>
				{part.length > 60 ? `${part.slice(0, 57)}…` : part}
			</a>
		) : (
			<span key={`${index}-t`}>{part}</span>
		),
	);
}

/** Grows a textarea with its content. */
function useAutosize(ref: React.RefObject<HTMLTextAreaElement | null>, value: string) {
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		element.style.height = 'auto';
		element.style.height = `${element.scrollHeight}px`;
		element.style.overflowY = 'hidden';
	}, [ref, value]);
}

/**
 * Escape inside an inline input should undo that input, not close the task.
 * The app's LayerManager takes Escape on document capture, so listen on window
 * capture (which runs first) while the input has focus.
 */
function useEscapeInside(ref: React.RefObject<HTMLElement | null>, onEscape: () => boolean) {
	const handler = useRef(onEscape);
	handler.current = onEscape;
	useEffect(() => {
		const listener = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || !ref.current || event.target !== ref.current) return;
			if (!handler.current()) return;
			event.preventDefault();
			event.stopImmediatePropagation();
		};
		window.addEventListener('keydown', listener, {capture: true});
		return () => window.removeEventListener('keydown', listener, {capture: true});
	}, [ref]);
}

export function SectionHead({
	title,
	count,
	end,
	className,
}: {
	title: string;
	count?: React.ReactNode;
	end?: React.ReactNode;
	className?: string;
}) {
	return (
		<div className={clsx(styles.sec, className)}>
			<h3 className={styles.secTitle}>{title}</h3>
			{count != null && <span className={styles.secCount}>{count}</span>}
			{end != null && <span className={styles.secEnd}>{end}</span>}
		</div>
	);
}

// ── Editor triggers: bottom sheet on the phone, popover on the desktop ─────

export type TaskEditorKind = 'status' | 'assignees' | 'due' | 'priority' | 'time' | 'mention' | 'more';

export interface EditorHost {
	mobile: boolean;
	/** Phone: opens the bottom sheet for this editor. */
	openSheet: (kind: TaskEditorKind) => void;
	/** Desktop: renders the editor inside a popover. */
	renderEditor: (kind: TaskEditorKind, close: () => void) => React.ReactNode;
}

export function EditTrigger({
	host,
	kind,
	position = 'bottom-end',
	children,
}: {
	host: EditorHost;
	kind: TaskEditorKind;
	position?: PopoutPosition;
	children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
}) {
	if (host.mobile) {
		return React.cloneElement(children, {
			onClick: () => host.openSheet(kind),
			'aria-haspopup': 'dialog',
		} as React.HTMLAttributes<HTMLElement>);
	}
	return (
		<Popout
			position={position}
			offsetMainAxis={6}
			render={({onClose}) => (
				<div className={clsx(styles.surface, styles.popover)}>{host.renderEditor(kind, onClose)}</div>
			)}
		>
			{children}
		</Popout>
	);
}

// ── Header: title, meta, status ──────────────────────────────────────────

export const TaskTitle = observer(({ws, className}: {ws: TaskWorkspace; className?: string}) => {
	const ref = useRef<HTMLTextAreaElement | null>(null);
	useAutosize(ref, ws.title);
	useEscapeInside(ref, () => {
		if (!ws.task || ws.title === ws.task.name) return false;
		ws.setTitle(ws.task.name);
		return true;
	});
	return (
		<textarea
			ref={ref}
			rows={1}
			className={clsx(styles.title, className)}
			value={ws.title}
			onChange={(event) => ws.setTitle(event.target.value.replace(/\n/g, ' '))}
			onBlur={ws.commitTitle}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					event.preventDefault();
					(event.target as HTMLTextAreaElement).blur();
				}
			}}
			aria-label="Task name"
			data-flx="konvo-tasks.task-modal.title"
		/>
	);
});

export const TaskMeta = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	const task = ws.task;
	if (!task) return null;
	const key = task.custom_id ?? task.id ?? ws.currentId;
	return (
		<div className={styles.meta}>
			<button
				type="button"
				className={styles.metaKey}
				onClick={() => void ws.copyId()}
				data-flx="konvo-tasks.task-modal.copy-id"
			>
				{key}
			</button>
			{task.creator?.username && (
				<span>
					{' · '}
					{i18n._(CREATED_BY_DESCRIPTOR, {name: task.creator.username})}
				</span>
			)}
		</div>
	);
});

export const StatusRow = observer(({ws, host}: {ws: TaskWorkspace; host: EditorHost}) => {
	const {i18n} = useLingui();
	const task = ws.task;
	if (!task) return null;
	return (
		<div className={styles.statusRow}>
			<EditTrigger host={host} kind="status" position="bottom-start">
				<button
					type="button"
					className={styles.statusPill}
					disabled={ws.saving === 'status' || !ws.detail}
					data-flx="konvo-tasks.task-modal.status-pill"
				>
					<span className={styles.dot} style={{background: task.status.color ?? '#8a8a92'}} />
					{statusLabel(task.status.status) || i18n._(NONE_DESCRIPTOR)}
					<CaretDownIcon size={14} weight="bold" />
				</button>
			</EditTrigger>
			{ws.closed ? (
				<span className={styles.donePill}>
					<CheckCircleIcon size={16} weight="fill" />
					{i18n._(COMPLETED_DESCRIPTOR)}
				</span>
			) : (
				<ActionButton
					className={styles.tonal}
					disabled={ws.saving === 'status'}
					onClick={() => void ws.complete()}
					icon={<CheckIcon size={16} weight="bold" />}
					data-flx="konvo-tasks.task-modal.complete"
				>
					{i18n._(MARK_COMPLETE_DESCRIPTOR)}
				</ActionButton>
			)}
		</div>
	);
});

// ── Property rows ────────────────────────────────────────────────────────

const PropRow = React.forwardRef<
	HTMLButtonElement,
	React.ButtonHTMLAttributes<HTMLButtonElement> & {icon: React.ReactNode; label: string}
>(({icon, label, children, ...rest}, ref) => (
	<button ref={ref} type="button" className={clsx(styles.prop, styles.propButton)} {...rest}>
		<span className={styles.propIcon}>{icon}</span>
		<span className={styles.propKey}>{label}</span>
		<span className={styles.propValue}>{children}</span>
	</button>
));
PropRow.displayName = 'PropRow';

export const TimerPill = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	if (ws.runningHere) {
		return (
			<ActionButton
				small
				tone="primary"
				className={styles.timePill}
				disabled={ws.saving === 'timer'}
				onClick={ws.stopTimer}
				icon={<StopIcon size={13} weight="fill" />}
				data-flx="konvo-tasks.task-modal.timer-stop"
			>
				{i18n._(STOP_DESCRIPTOR)}
			</ActionButton>
		);
	}
	const elsewhere = ws.runningEntry != null;
	return (
		<ActionButton
			small
			className={clsx(styles.tonal, styles.timePill)}
			disabled={ws.saving === 'timer' || !ws.timerAvailable || elsewhere}
			title={
				!ws.timerAvailable
					? i18n._(TIMER_LINK_REQUIRED_DESCRIPTOR)
					: elsewhere
						? i18n._(TIMER_ELSEWHERE_DESCRIPTOR, {name: ws.runningEntry?.task.name ?? ''})
						: undefined
			}
			onClick={ws.startTimer}
			icon={<PlayIcon size={14} />}
			data-flx="konvo-tasks.task-modal.timer-start"
		>
			{i18n._(START_DESCRIPTOR)}
		</ActionButton>
	);
});

export const PropertyCard = observer(({ws, host}: {ws: TaskWorkspace; host: EditorHost}) => {
	const {i18n} = useLingui();
	const task = ws.task;
	if (!task) return null;
	const tone = dueTone(task);
	const total = ws.timeInfo?.total_ms ?? task.time_spent ?? 0;
	const runningMs = ws.runningHere ? ws.now - (ws.runningEntry?.start ?? ws.now) : 0;
	return (
		<div className={styles.card} data-flx="konvo-tasks.task-modal.properties">
			<EditTrigger host={host} kind="assignees">
				<PropRow
					icon={<UsersIcon size={20} />}
					label={i18n._(TASK_DESCRIPTORS.assignees)}
					data-flx="konvo-tasks.task-modal.assignees"
				>
					{task.assignees.length > 0 ? (
						<AvatarPile users={task.assignees} />
					) : (
						<span className={styles.muted}>{i18n._(ADD_DESCRIPTOR)}</span>
					)}
				</PropRow>
			</EditTrigger>
			<EditTrigger host={host} kind="due">
				<PropRow
					icon={<CalendarBlankIcon size={20} />}
					label={i18n._(TASK_DESCRIPTORS.due)}
					data-flx="konvo-tasks.task-modal.due"
				>
					{task.due_date != null ? (
						<span className={clsx(tone && styles.danger)}>{fmtDue(task)}</span>
					) : (
						<span className={styles.muted}>{i18n._(NO_DUE_DESCRIPTOR)}</span>
					)}
				</PropRow>
			</EditTrigger>
			<EditTrigger host={host} kind="priority">
				<PropRow
					icon={<FlagIcon size={20} />}
					label={i18n._(TASK_DESCRIPTORS.priority)}
					data-flx="konvo-tasks.task-modal.priority"
				>
					{task.priority?.id != null && PRIORITY_DESCRIPTORS[task.priority.id] ? (
						<>
							<PriorityFlag id={task.priority.id} />
							{i18n._(PRIORITY_DESCRIPTORS[task.priority.id]!)}
						</>
					) : (
						<span className={styles.muted}>{i18n._(NONE_DESCRIPTOR)}</span>
					)}
				</PropRow>
			</EditTrigger>
			{task.tags.length > 0 && (
				<div className={styles.prop}>
					<span className={styles.propIcon}>
						<TagIcon size={20} />
					</span>
					<span className={styles.propKey}>{i18n._(TASK_DESCRIPTORS.tags)}</span>
					<span className={clsx(styles.propValue, styles.tagList)}>
						{task.tags.map((tag) => (
							<span key={tag.name ?? ''} className={styles.tag}>
								<span className={styles.dot} style={{background: tag.color ?? 'currentColor'}} />
								{tag.name}
							</span>
						))}
					</span>
				</div>
			)}
			<div className={styles.prop}>
				<span className={styles.propIcon}>
					<ClockIcon size={20} />
				</span>
				<span className={styles.propKey}>{i18n._(TASK_DESCRIPTORS.time)}</span>
				<span className={styles.propValue}>
					{ws.runningHere ? (
						<span className={styles.liveClock} role="timer">
							{fmtTimer(runningMs)}
						</span>
					) : (
						<span className={styles.muted}>{i18n._(LOGGED_DESCRIPTOR, {duration: formatDuration(total)})}</span>
					)}
					<TimerPill ws={ws} />
				</span>
			</div>
		</div>
	);
});

// ── Description, attachments ─────────────────────────────────────────────

export const DescriptionSection = observer(({ws, mobile}: {ws: TaskWorkspace; mobile: boolean}) => {
	const {i18n} = useLingui();
	const ref = useRef<HTMLTextAreaElement | null>(null);
	const fileRef = useRef<HTMLInputElement | null>(null);
	// ClickUp descriptions are markdown: they read formatted, and the source opens on a click.
	const [editing, setEditing] = useState(false);
	const showEditor = editing || ws.descDirty || !ws.description.trim();
	useAutosize(ref, showEditor ? ws.description : '');
	useEffect(() => setEditing(false), [ws.currentId]);
	useEffect(() => {
		if (!editing) return;
		const input = ref.current;
		input?.focus();
		input?.setSelectionRange(input.value.length, input.value.length);
	}, [editing]);
	useEscapeInside(ref, () => {
		if (!editing || ws.descDirty) return false;
		setEditing(false);
		return true;
	});
	const stopEditing = () => setEditing(false);
	return (
		<section>
			<SectionHead
				title={i18n._(TASK_DESCRIPTORS.description)}
				end={
					<>
						{!showEditor && (
							<IconButton
								label={i18n._(EDIT_DESCRIPTION_DESCRIPTOR)}
								icon={<PencilSimpleIcon size={16} />}
								className={styles.secButton}
								onClick={() => setEditing(true)}
								data-flx="konvo-tasks.task-modal.description-edit"
							/>
						)}
						{!mobile && (
							<IconButton
								label={i18n._(TASK_DESCRIPTORS.attach)}
								icon={<PaperclipIcon size={16} />}
								className={styles.secButton}
								disabled={ws.descDrop.uploading}
								onClick={() => fileRef.current?.click()}
								data-flx="konvo-tasks.task-modal.description-attach"
							/>
						)}
					</>
				}
			/>
			<input
				ref={fileRef}
				type="file"
				multiple
				hidden
				data-task-description-file=""
				onChange={(event) => {
					void ws.descDrop.upload(event.target.files);
					event.target.value = '';
				}}
			/>
			{showEditor ? (
				<div className={clsx(styles.card, styles.descCard, ws.descDrop.dragging && styles.dropActive)}>
					<textarea
						ref={ref}
						rows={2}
						className={styles.desc}
						placeholder={i18n._(mobile ? DESCRIPTION_PLACEHOLDER_DESCRIPTOR : DESCRIPTION_DROP_PLACEHOLDER_DESCRIPTOR)}
						value={ws.description}
						onChange={(event) => ws.editDescription(event.target.value)}
						onBlur={() => {
							if (!ws.descDirty) stopEditing();
						}}
						{...ws.descDrop.dropProps}
						data-flx="konvo-tasks.task-modal.description"
					/>
				</div>
			) : (
				// biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a pointer shortcut; keyboard users edit with the pencil button.
				<div
					className={clsx(
						styles.card,
						styles.descCard,
						styles.descView,
						markupStyles.markup,
						ws.descDrop.dragging && styles.dropActive,
					)}
					onClick={(event) => {
						// Links open; selecting text to copy it stays a selection.
						if ((event.target as HTMLElement).closest('a, button')) return;
						if (window.getSelection()?.toString()) return;
						setEditing(true);
					}}
					{...ws.descDrop.dropProps}
					data-flx="konvo-tasks.task-modal.description-view"
				>
					<TaskDescriptionMarkdown text={ws.description} images={ws.task?.description_images} />
				</div>
			)}
			{ws.descDirty && (
				<div className={styles.inlineActions}>
					<ActionButton
						small
						tone="quiet"
						onClick={() => {
							ws.discardDescription();
							stopEditing();
						}}
						data-flx="konvo-tasks.task-modal.description-cancel"
					>
						{i18n._(CANCEL_DESCRIPTOR)}
					</ActionButton>
					<ActionButton
						small
						tone="primary"
						disabled={ws.saving === 'description'}
						onClick={() =>
							void ws.saveDescription().then(() => {
								stopEditing();
							})
						}
						data-flx="konvo-tasks.task-modal.description-save"
					>
						{i18n._(SAVE_DESCRIPTOR)}
					</ActionButton>
				</div>
			)}
		</section>
	);
});

export const AttachmentsSection = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	const attachments = ws.task?.attachments ?? [];
	if (attachments.length === 0) return null;
	return (
		<section>
			<SectionHead title={i18n._(TASK_DESCRIPTORS.attachments)} count={attachments.length} />
			<div className={styles.card}>
				{attachments.map((attachment) => (
					<button
						key={attachment.id ?? attachment.url ?? attachment.title}
						type="button"
						className={clsx(styles.prop, styles.propButton, styles.file)}
						onClick={() => openTaskAttachment(attachment, attachments)}
						data-flx="konvo-tasks.task-modal.attachment"
					>
						{attachment.thumbnail ? (
							<Picture src={attachment.thumbnail} alt="" className={styles.fileThumb} />
						) : (
							<span className={styles.fileThumb}>
								<FileIcon size={18} />
							</span>
						)}
						<span className={styles.fileMeta}>
							<span className={styles.fileName}>{attachment.title}</span>
							<span className={styles.muted}>
								{[fmtBytes(attachment.size), attachment.date ? fmtRelative(attachment.date) : null]
									.filter(Boolean)
									.join(' · ')}
							</span>
						</span>
					</button>
				))}
			</div>
		</section>
	);
});

// ── Subtasks ─────────────────────────────────────────────────────────────

export const SubtasksSection = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	const [adding, setAdding] = useState(false);
	const [draft, setDraft] = useState('');
	const inputRef = useRef<HTMLInputElement | null>(null);
	useEffect(() => {
		if (adding) inputRef.current?.focus();
	}, [adding]);
	useEscapeInside(inputRef, () => {
		setDraft('');
		setAdding(false);
		return true;
	});
	const task = ws.task;
	if (!task) return null;
	const {done, total} = subtaskProgress(task.subtasks);
	const submit = async () => {
		if (ws.saving === 'subtask:new') return;
		if (!draft.trim()) {
			setAdding(false);
			return;
		}
		if (await ws.addSubtask(draft)) {
			setDraft('');
			inputRef.current?.focus();
		}
	};
	return (
		<section data-flx="konvo-tasks.task-modal.subtasks">
			<SectionHead
				title={i18n._(TASK_DESCRIPTORS.subtasks)}
				count={total > 0 ? i18n._(PROGRESS_DESCRIPTOR, {done, total}) : undefined}
				end={
					<IconButton
						label={i18n._(ADD_SUBTASK_DESCRIPTOR)}
						icon={<PlusIcon size={18} />}
						className={styles.secButton}
						onClick={() => setAdding(true)}
						data-flx="konvo-tasks.task-modal.subtask-add"
					/>
				}
			/>
			{total > 0 && (
				<div
					className={styles.progress}
					role="progressbar"
					aria-valuemin={0}
					aria-valuemax={total}
					aria-valuenow={done}
				>
					<i style={{width: `${(done / total) * 100}%`}} />
				</div>
			)}
			{(total > 0 || adding) && (
				<div className={styles.card}>
					{task.subtasks.map((subtask) => {
						const closed = isTaskClosed(subtask);
						return (
							<div
								key={subtask.id ?? subtask.name}
								className={clsx(styles.sub, closed && styles.subDone)}
								data-flx="konvo-tasks.task-modal.subtask"
							>
								<button
									type="button"
									role="checkbox"
									aria-checked={closed}
									aria-label={i18n._(closed ? MARK_SUBTASK_OPEN_DESCRIPTOR : MARK_SUBTASK_DONE_DESCRIPTOR, {
										name: subtask.name,
									})}
									className={clsx(styles.check, closed && styles.checkOn)}
									disabled={!subtask.id || ws.saving === `sub:${subtask.id}`}
									onClick={() => void ws.toggleSubtask(subtask)}
									data-flx="konvo-tasks.task-modal.subtask-check"
								>
									{closed && <CheckIcon size={13} weight="bold" />}
								</button>
								<button
									type="button"
									className={styles.subName}
									onClick={() => subtask.id && ws.openTask(subtask.id)}
									data-flx="konvo-tasks.task-modal.subtask-open"
								>
									{subtask.name}
								</button>
								{subtask.assignees.length > 0 && !closed && (
									<span className={styles.subWho}>
										<UserAvatar user={subtask.assignees[0]!} size={22} />
									</span>
								)}
							</div>
						);
					})}
					{adding && (
						<div className={styles.sub}>
							<span className={clsx(styles.check, styles.checkGhost)} aria-hidden="true">
								<PlusIcon size={13} />
							</span>
							<input
								ref={inputRef}
								className={styles.subInput}
								value={draft}
								placeholder={i18n._(SUBTASK_PLACEHOLDER_DESCRIPTOR)}
								aria-label={i18n._(ADD_SUBTASK_DESCRIPTOR)}
								readOnly={ws.saving === 'subtask:new'}
								enterKeyHint="done"
								onChange={(event) => setDraft(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										event.preventDefault();
										void submit();
									}
								}}
								onBlur={() => {
									if (!draft.trim()) setAdding(false);
								}}
								data-flx="konvo-tasks.task-modal.subtask-input"
							/>
						</div>
					)}
				</div>
			)}
		</section>
	);
});

// ── Time tracking ────────────────────────────────────────────────────────

export const TimeSection = observer(
	({
		ws,
		onLog,
		logForm,
	}: {
		ws: TaskWorkspace;
		onLog: (entry: KonvoTimeEntry | null) => void;
		logForm?: React.ReactNode;
	}) => {
		const {i18n} = useLingui();
		const task = ws.task;
		if (!task) return null;
		const total = ws.timeInfo?.total_ms ?? task.time_spent ?? 0;
		const entries = (ws.timeInfo?.entries ?? []).filter((entry) => !entry.running);
		const totalLabel =
			total > 0
				? `${i18n._(TOTAL_DESCRIPTOR, {duration: formatDuration(total)})}${
						task.time_estimate != null
							? ` ${i18n._(ESTIMATE_DESCRIPTOR, {estimate: formatDuration(task.time_estimate)})}`
							: ''
					}`
				: undefined;
		return (
			<section data-flx="konvo-tasks.task-modal.time">
				<SectionHead title={i18n._(TASK_DESCRIPTORS.timeTracking)} end={totalLabel} />
				<div className={styles.card}>
					{ws.runningHere ? (
						<div className={styles.timer}>
							<span className={styles.timerBig} role="timer" aria-label={i18n._(TASK_DESCRIPTORS.timeTracking)}>
								{fmtTimer(ws.now - (ws.runningEntry?.start ?? ws.now))}
							</span>
							<span className={styles.timerMeta}>
								{i18n._(RUNNING_SINCE_DESCRIPTOR, {time: fmtTime(ws.runningEntry?.start ?? ws.now)})}
							</span>
							<IconButton
								label={i18n._(PAUSE_DESCRIPTOR)}
								icon={<PauseIcon size={18} weight="fill" />}
								className={styles.tonalRound}
								disabled={ws.saving === 'timer'}
								onClick={ws.pauseTimer}
								data-flx="konvo-tasks.task-modal.timer-pause"
							/>
							<IconButton
								label={i18n._(STOP_DESCRIPTOR)}
								icon={<StopIcon size={18} weight="fill" />}
								primary
								className={styles.inkRound}
								disabled={ws.saving === 'timer'}
								onClick={ws.stopTimer}
								data-flx="konvo-tasks.task-modal.timer-stop-big"
							/>
						</div>
					) : (
						<div className={styles.timerIdle}>
							<ActionButton
								small
								className={styles.tonal}
								disabled={ws.saving === 'timer' || !ws.timerAvailable || ws.runningEntry != null}
								onClick={ws.startTimer}
								icon={<PlayIcon size={14} />}
							>
								{i18n._(START_TIMER_DESCRIPTOR)}
							</ActionButton>
							<ActionButton
								small
								className={styles.tonal}
								onClick={() => onLog(null)}
								icon={<PlusIcon size={14} />}
								data-flx="konvo-tasks.task-modal.time-log-open"
							>
								{i18n._(TASK_DESCRIPTORS.logTime)}
							</ActionButton>
						</div>
					)}
					{!ws.timerAvailable && <p className={styles.hint}>{i18n._(TIMER_LINK_REQUIRED_DESCRIPTOR)}</p>}
					{ws.timerAvailable && ws.runningEntry != null && !ws.runningHere && (
						<p className={styles.hint}>
							{i18n._(TIMER_ELSEWHERE_DESCRIPTOR, {name: ws.runningEntry.task.name ?? ws.runningEntry.task.id ?? ''})}
						</p>
					)}
					{logForm}
					{entries.length === 0 && !logForm && !ws.runningHere && (
						<SectionEmpty
							icon={<IconClock size={15} stroke={1.8} />}
							text={i18n._(NO_TIME_DESCRIPTOR)}
							flx="konvo-tasks.task-modal.time-empty"
						/>
					)}
					{entries.map((entry) => {
						const own = ws.isOwnEntry(entry);
						const who = own ? null : (entry.user?.username ?? entry.user?.email ?? null);
						const content = (
							<>
								<span className={styles.propIcon}>
									<ClockIcon size={20} />
								</span>
								<span className={styles.entryBody}>
									<span className={styles.entryDay}>{entry.start != null ? fmtDay(entry.start) : '—'}</span>
									{(entry.description || who) && (
										<span className={styles.entryNote}>{[who, entry.description].filter(Boolean).join(' · ')}</span>
									)}
								</span>
								<span className={styles.entryDuration}>{formatDuration(entry.duration)}</span>
							</>
						);
						return own ? (
							<button
								key={entry.id ?? `${entry.start}`}
								type="button"
								className={clsx(styles.prop, styles.propButton, styles.entry)}
								aria-label={i18n._(TASK_DESCRIPTORS.editEntry)}
								disabled={ws.saving != null}
								onClick={() => onLog(entry)}
								data-flx="konvo-tasks.task-modal.time-entry"
							>
								{content}
							</button>
						) : (
							<div
								key={entry.id ?? `${entry.start}`}
								className={clsx(styles.prop, styles.entry)}
								data-flx="konvo-tasks.task-modal.time-entry"
							>
								{content}
							</div>
						);
					})}
				</div>
			</section>
		);
	},
);

/** Manual time entry form: in a sheet on the phone, inline in the time card on the desktop. */
export const TimeLogForm = observer(
	({ws, onDone, inline = false}: {ws: TaskWorkspace; onDone: () => void; inline?: boolean}) => {
		const {i18n} = useLingui();
		const draft = ws.timeDraft;
		const set = (patch: Partial<typeof draft>) => ws.setTimeDraft({...draft, ...patch});
		const submit = async () => {
			if (await ws.submitLog()) onDone();
		};
		return (
			<div className={clsx(styles.form, inline && styles.formInline)} data-flx="konvo-tasks.task-modal.time-form">
				<label className={styles.field}>
					<span className={styles.fieldLabel}>{i18n._(DURATION_LABEL_DESCRIPTOR)}</span>
					<input
						className={styles.input}
						value={draft.duration}
						placeholder={i18n._(DURATION_PLACEHOLDER_DESCRIPTOR)}
						inputMode="text"
						onChange={(event) => set({duration: event.target.value})}
						onKeyDown={(event) => {
							if (event.key === 'Enter') void submit();
						}}
						data-flx="konvo-tasks.task-modal.time-duration"
					/>
				</label>
				<div className={styles.quickRow}>
					{['15m', '30m', '1h', '2h'].map((value) => (
						<button
							key={value}
							type="button"
							className={clsx(styles.quick, draft.duration === value && styles.quickOn)}
							onClick={() => set({duration: value})}
						>
							{value}
						</button>
					))}
				</div>
				<label className={styles.field}>
					<span className={styles.fieldLabel}>{i18n._(WHEN_LABEL_DESCRIPTOR)}</span>
					<input
						type="datetime-local"
						className={styles.input}
						value={draft.start}
						onChange={(event) => set({start: event.target.value})}
						data-flx="konvo-tasks.task-modal.time-start"
					/>
					{!draft.entry && <span className={styles.fieldHint}>{i18n._(WHEN_HINT_DESCRIPTOR)}</span>}
				</label>
				<label className={styles.field}>
					<span className={styles.fieldLabel}>{i18n._(NOTE_LABEL_DESCRIPTOR)}</span>
					<input
						className={styles.input}
						value={draft.note}
						placeholder={i18n._(NOTE_PLACEHOLDER_DESCRIPTOR)}
						onChange={(event) => set({note: event.target.value})}
						onKeyDown={(event) => {
							if (event.key === 'Enter') void submit();
						}}
						data-flx="konvo-tasks.task-modal.time-note"
					/>
				</label>
				<div className={styles.formActions}>
					{draft.entry && (
						<ActionButton
							small={inline}
							tone="danger"
							icon={<TrashIcon size={15} />}
							disabled={ws.saving != null}
							onClick={() => {
								const entry = draft.entry;
								if (!entry) return;
								void ws.deleteEntry(entry).then((ok) => {
									if (!ok) return;
									ws.beginLog(null);
									onDone();
								});
							}}
							data-flx="konvo-tasks.task-modal.time-entry-delete"
						>
							{i18n._(DELETE_ENTRY_DESCRIPTOR)}
						</ActionButton>
					)}
					<span className={styles.grow} />
					{inline && (
						<ActionButton
							small
							tone="quiet"
							onClick={() => {
								ws.beginLog(null);
								onDone();
							}}
						>
							{i18n._(CANCEL_DESCRIPTOR)}
						</ActionButton>
					)}
					<ActionButton
						small={inline}
						block={!inline}
						tone="primary"
						disabled={!draft.duration.trim() || ws.saving != null}
						onClick={() => void submit()}
						data-flx="konvo-tasks.task-modal.time-log"
					>
						{i18n._(draft.entry ? SAVE_DESCRIPTOR : TASK_DESCRIPTORS.logTime)}
					</ActionButton>
				</div>
			</div>
		);
	},
);

// ── Activity + composer ──────────────────────────────────────────────────

export const ActivitySection = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	const task = ws.task;
	if (!task) return null;
	const creatorName = firstName(task.creator?.username);
	return (
		<section data-flx="konvo-tasks.task-modal.activity">
			<SectionHead title={i18n._(TASK_DESCRIPTORS.activity)} count={ws.comments.length || undefined} />
			<div className={clsx(styles.card, styles.activity)}>
				{task.creator && (
					<div className={styles.sys}>
						<SparkleIcon size={15} />
						<span>
							<Trans comment="Task activity line. The bold part is the person who created the task.">
								<b>{creatorName}</b> created this task
							</Trans>
							{task.date_created != null && ` · ${fmtShortDay(task.date_created)}`}
						</span>
					</div>
				)}
				{!ws.detail && !ws.loadError && <CommentsSkeleton label={i18n._(LOADING_COMMENTS_DESCRIPTOR)} />}
				{ws.detail && ws.comments.length === 0 && (
					<SectionEmpty
						icon={<IconMessageCircle size={15} stroke={1.8} />}
						text={i18n._(NO_COMMENTS_DESCRIPTOR)}
						flx="konvo-tasks.task-modal.comments-empty"
					/>
				)}
				{ws.comments.map((entry) => (
					<div key={entry.id ?? `${entry.date}`} className={styles.act} data-flx="konvo-tasks.task-modal.comment">
						<UserAvatar user={entry.user} size={30} />
						<div className={styles.actBody}>
							<div className={styles.actHead}>
								<b>{entry.user?.username ?? entry.user?.email ?? '—'}</b>
								<span className={styles.actTime}>{fmtStamp(entry.date)}</span>
							</div>
							<p className={styles.actText}>{linkify(entry.text)}</p>
						</div>
					</div>
				))}
			</div>
		</section>
	);
});

export const Composer = observer(({ws, host, className}: {ws: TaskWorkspace; host: EditorHost; className?: string}) => {
	const {i18n} = useLingui();
	const ref = useRef<HTMLTextAreaElement | null>(null);
	const fileRef = useRef<HTMLInputElement | null>(null);
	useLayoutEffect(() => {
		if (ref.current) fitComposerHeight(ref.current);
	}, [ws.comment]);
	const typed = ws.comment.trim().length > 0;
	return (
		<div
			className={clsx(styles.composer, ws.commentDrop.dragging && styles.dropActive, className)}
			{...ws.commentDrop.dropProps}
		>
			<textarea
				ref={ref}
				rows={1}
				className={styles.composerInput}
				placeholder={i18n._(COMMENT_PLACEHOLDER_DESCRIPTOR)}
				value={ws.comment}
				onChange={(event) => ws.setComment(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
						event.preventDefault();
						void ws.submitComment();
					}
				}}
				data-composer-input=""
				data-flx="konvo-tasks.task-modal.comment-input"
			/>
			{(!typed || !host.mobile) && (
				<>
					<EditTrigger host={host} kind="mention" position="top-end">
						<button
							type="button"
							className={styles.composerIcon}
							aria-label={i18n._(TASK_DESCRIPTORS.mention)}
							title={i18n._(TASK_DESCRIPTORS.mention)}
							data-flx="konvo-tasks.task-modal.comment-mention"
						>
							<AtIcon size={20} />
						</button>
					</EditTrigger>
					<button
						type="button"
						className={styles.composerIcon}
						onClick={() => fileRef.current?.click()}
						disabled={ws.commentDrop.uploading}
						aria-label={i18n._(TASK_DESCRIPTORS.attach)}
						title={i18n._(TASK_DESCRIPTORS.attach)}
						data-flx="konvo-tasks.task-modal.comment-attach"
					>
						<PaperclipIcon size={20} />
					</button>
				</>
			)}
			<input
				ref={fileRef}
				type="file"
				multiple
				hidden
				onChange={(event) => {
					void ws.commentDrop.upload(event.target.files);
					event.target.value = '';
				}}
			/>
			{typed && (
				<button
					type="button"
					className={styles.send}
					aria-label={i18n._(SEND_COMMENT_DESCRIPTOR)}
					disabled={ws.saving === 'comment'}
					onClick={() => void ws.submitComment()}
					data-flx="konvo-tasks.task-modal.comment-send"
				>
					<ArrowUpIcon size={18} weight="bold" />
				</button>
			)}
		</div>
	);
});

// ── Editors ──────────────────────────────────────────────────────────────

function Option({
	lead,
	label,
	selected,
	onClick,
	...rest
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
	lead?: React.ReactNode;
	label: React.ReactNode;
	selected?: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			role="menuitemradio"
			aria-checked={selected ?? false}
			className={clsx(styles.option, selected && styles.optionOn)}
			onClick={onClick}
			{...rest}
		>
			{lead != null && <span className={styles.optionLead}>{lead}</span>}
			<span className={styles.optionLabel}>{label}</span>
			<span className={styles.optionCheck}>{selected && <CheckIcon size={18} weight="bold" />}</span>
		</button>
	);
}

export const StatusOptions = observer(({ws, close}: {ws: TaskWorkspace; close: () => void}) => {
	const task = ws.task;
	return (
		<div className={styles.options} role="menu">
			{(ws.detail?.statuses ?? []).map((status) => (
				<Option
					key={status.status ?? ''}
					lead={<span className={styles.dotLg} style={{background: status.color ?? '#8a8a92'}} />}
					label={statusLabel(status.status)}
					selected={status.status === task?.status.status}
					onClick={() => {
						close();
						ws.setStatus(status.status);
					}}
					data-flx="konvo-tasks.task-modal.status-option"
				/>
			))}
		</div>
	);
});

export const PriorityOptions = observer(({ws, close}: {ws: TaskWorkspace; close: () => void}) => {
	const {i18n} = useLingui();
	const current = ws.task?.priority?.id ?? null;
	return (
		<div className={styles.options} role="menu">
			{TASK_PRIORITIES.map((priority) => (
				<Option
					key={priority.id}
					lead={<PriorityFlag id={priority.id} size={18} />}
					label={i18n._(PRIORITY_DESCRIPTORS[priority.id]!)}
					selected={current === priority.id}
					onClick={() => {
						close();
						ws.setPriority(priority.id);
					}}
					data-flx="konvo-tasks.task-modal.priority-option"
				/>
			))}
			<Option
				lead={<FlagIcon size={18} />}
				label={i18n._(NONE_DESCRIPTOR)}
				selected={current == null}
				onClick={() => {
					close();
					ws.setPriority(null);
				}}
			/>
		</div>
	);
});

function memberAsUser(
	member: {id: number; username: string; email: string},
	assignees: Array<KonvoTaskUser>,
): KonvoTaskUser {
	return (
		assignees.find((assignee) => assignee.id === member.id) ?? {
			id: member.id,
			username: member.username,
			email: member.email,
			initials: null,
			color: null,
			profile_picture: null,
		}
	);
}

export const AssigneeOptions = observer(({ws}: {ws: TaskWorkspace}) => {
	const {i18n} = useLingui();
	const assignees = ws.task?.assignees ?? [];
	// Assigned people first, then everyone else, each alphabetically.
	const members = [...ws.members].sort((a, b) => {
		const aOn = assignees.some((x) => x.id === a.id) ? 0 : 1;
		const bOn = assignees.some((x) => x.id === b.id) ? 0 : 1;
		return aOn - bOn || (a.username || a.email).localeCompare(b.username || b.email);
	});
	// Assignees ClickUp knows but the member list lacks (guests) still need a way off the task.
	const extra = assignees.filter((assignee) => assignee.id != null && !ws.members.some((m) => m.id === assignee.id));
	return (
		<div className={styles.options} role="menu" data-flx="konvo-tasks.task-modal.assignee-options">
			{extra.map((assignee) => (
				<Option
					key={`x-${assignee.id}`}
					lead={<UserAvatar user={assignee} size={28} />}
					label={assignee.username ?? assignee.email}
					selected
					disabled={ws.saving === 'assignees'}
					onClick={() => assignee.id != null && ws.toggleAssignee(assignee.id)}
				/>
			))}
			{members.map((member) => (
				<Option
					key={member.id}
					lead={<UserAvatar user={memberAsUser(member, assignees)} size={28} />}
					label={member.username || member.email}
					selected={assignees.some((assignee) => assignee.id === member.id)}
					disabled={ws.saving === 'assignees'}
					onClick={() => ws.toggleAssignee(member.id)}
					data-flx="konvo-tasks.task-modal.assignee-option"
				/>
			))}
			{members.length === 0 && extra.length === 0 && <p className={styles.hint}>{i18n._(NO_MEMBERS_DESCRIPTOR)}</p>}
		</div>
	);
});

export const MentionOptions = observer(({ws, close}: {ws: TaskWorkspace; close: () => void}) => {
	const {i18n} = useLingui();
	const assignees = ws.task?.assignees ?? [];
	return (
		<div className={styles.options} role="menu">
			{ws.members.map((member) => (
				<Option
					key={member.id}
					lead={<UserAvatar user={memberAsUser(member, assignees)} size={28} />}
					label={member.username || member.email}
					onClick={() => {
						close();
						const name = member.username || member.email;
						ws.setComment(`${ws.comment}${ws.comment && !/\s$/.test(ws.comment) ? ' ' : ''}@${name} `);
						requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>('[data-composer-input]')?.focus());
					}}
				/>
			))}
			{ws.members.length === 0 && <p className={styles.hint}>{i18n._(NO_MEMBERS_DESCRIPTOR)}</p>}
		</div>
	);
});

export const DueEditor = observer(({ws, close}: {ws: TaskWorkspace; close: () => void}) => {
	const {i18n} = useLingui();
	const task = ws.task;
	const [date, setDate] = useState(toDateInputValue(task?.due_date ?? null));
	const [time, setTime] = useState(task?.due_date_time ? toTimeInputValue(task.due_date) : '');
	if (!task) return null;
	const apply = (nextDate: string, nextTime: string) => {
		const ms = fromDateAndTime(nextDate, nextTime);
		if (ms == null) return;
		ws.setDue(ms, Boolean(nextTime));
	};
	return (
		<div className={styles.form} data-flx="konvo-tasks.task-modal.due-editor">
			<div className={styles.quickRow}>
				{dueQuickPicks().map((pick) => {
					const value = toDateInputValue(pick.ms);
					return (
						<button
							key={pick.key}
							type="button"
							className={clsx(styles.quick, date === value && styles.quickOn)}
							onClick={() => {
								setDate(value);
								apply(value, time);
								close();
							}}
						>
							<span>{i18n._(DUE_PICK_DESCRIPTORS[pick.key])}</span>
							<small>{fmtDay(pick.ms)}</small>
						</button>
					);
				})}
			</div>
			<div className={styles.fieldRow}>
				<label className={styles.field}>
					<span className={styles.fieldLabel}>{i18n._(DATE_LABEL_DESCRIPTOR)}</span>
					<input
						type="date"
						className={styles.input}
						value={date}
						onChange={(event) => {
							setDate(event.target.value);
							apply(event.target.value, time);
						}}
						data-flx="konvo-tasks.task-modal.due-date"
					/>
				</label>
				<label className={styles.field}>
					<span className={styles.fieldLabel}>{i18n._(TIME_LABEL_DESCRIPTOR)}</span>
					<input
						type="time"
						className={styles.input}
						value={time}
						disabled={!date}
						onChange={(event) => {
							setTime(event.target.value);
							apply(date, event.target.value);
						}}
						data-flx="konvo-tasks.task-modal.due-time"
					/>
				</label>
			</div>
			{task.due_date != null && (
				<ActionButton
					tone="quiet"
					small
					onClick={() => {
						close();
						ws.setDue(null, false);
					}}
					data-flx="konvo-tasks.task-modal.clear-due"
				>
					{i18n._(CLEAR_DUE_DESCRIPTOR)}
				</ActionButton>
			)}
		</div>
	);
});
